import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== articleId).slice(0, 3);

  const articleLD = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '国产视频大全',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.guochanshipin.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleLD)}</script>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <article>
          <div className="pt-20 pb-0">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '文章', href: '/category/yingshiju' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="relative w-full h-[300px] md:h-[400px] mt-4 overflow-hidden">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto h-full">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 国产视频大全`}
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8 md:py-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                  {tag}
                </span>
              ))}
              <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 pb-6 mb-6 border-b border-background-200/70">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-user-line text-foreground-500"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">发布于 {article.publishDate}</p>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-h2:font-heading prose-h2:text-xl prose-h2:md:text-2xl prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-sm prose-p:md:text-base prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>阅读量: {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}